import React from 'react'

import { ChevronDown } from 'lucide-react'
import { cn } from '~/shadcn/lib/utils'
import { objectValues } from '~/@lib/utils/type.utils'
import { Button } from '~/shadcn/ui/button/button'

import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '~/shadcn/ui/dropdown-menu'
import { getProjectCollateralSubcategoryLabel } from '../_projects_ui/project-collateral/labelByCollateralSubcategory'
import { getProjectCollateralCategoryLabel } from '../_projects_ui/project-collateral/labelByCollateralCategory'
import { ProjectCollateralCategory } from '../_project_types/projectCollateralCategory'
import {
  ProjectCollateralSubcategoryMapping,
  type ProjectCollateralSubcategory,
} from '../_project_types/projectCollateralSubCategory'

import { colorByCollateralCategory } from './colorByCollateralCategory'

type Props = {
  colorScheme?: 'dark' | 'light'
  values: Array<ProjectCollateralSubcategory>
  onChange(value: Array<ProjectCollateralSubcategory>): void
}

const groups = objectValues(ProjectCollateralCategory).map((category) => ({
  value: category,
  options: ProjectCollateralSubcategoryMapping.get(category)!,
}))

export function CollateralSubcategorySelect({
  colorScheme = 'dark',
  values: selectedValues = [],
  onChange,
}: Props) {
  const someSelected = !!selectedValues.length

  const isDark = colorScheme === 'dark'

  const updateCategorySelection = (
    optionsWithinCategory: ProjectCollateralSubcategory[]
  ) => {
    const allOptionsSelected = optionsWithinCategory.every((option) =>
      selectedValues.includes(option)
    )

    if (allOptionsSelected) {
      const remainingOptions = selectedValues.filter(
        (option) => !optionsWithinCategory.includes(option)
      )
      onChange(remainingOptions)
      return
    }

    const combinedSelections = [
      ...new Set([...selectedValues, ...optionsWithinCategory]),
    ]
    onChange(combinedSelections)
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="filter"
          size="filter"
          className={cn(
            'max-w-fit rounded-lg px-3 py-2',
            'border-2 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0',
            someSelected
              ? 'border-accent bg-accent hover:bg-accent'
              : isDark
                ? 'border-border text-primary-foreground'
                : 'border-border-strong text-primary'
          )}>
          <div className="flex items-center justify-between gap-1">
            Typ zaistenia
            <ChevronDown className="h-6 w-6" />
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="mt-0.5 max-h-[372px] w-[270px] p-1 pb-2"
        style={{
          fontFamily: 'inherit',
        }}>
        {groups.map(({ value, options }, groupIndex) => (
          <React.Fragment key={`${value}${groupIndex}`}>
            <DropdownMenuLabel className="px-2 py-3">
              <div className="flex w-full items-center gap-2">
                <button
                  className="rounded-sm outline-none focus-visible:ring-2  focus-visible:ring-offset-2 focus-visible:ring-offset-black"
                  onClick={(event) => {
                    event.stopPropagation()
                    updateCategorySelection(options)
                  }}>
                  <CategoryLabel category={value} />
                </button>
                <div className="h-px grow bg-border-strong" />
              </div>
            </DropdownMenuLabel>
            {options.map((option, optionIndex) => (
              <DropdownMenuCheckboxItem
                key={`${optionIndex}`}
                className="cursor-pointer rounded-lg p-3 text-primary-foreground focus:bg-accent focus:text-black-900"
                checked={selectedValues.includes(option)}
                onCheckedChange={(checked) => {
                  if (checked) {
                    onChange([...new Set([...selectedValues, option])])
                  } else {
                    onChange(selectedValues.filter((v) => v !== option))
                  }
                }}
                onSelect={(e) => e.preventDefault()}>
                {getProjectCollateralSubcategoryLabel(option)}
              </DropdownMenuCheckboxItem>
            ))}
          </React.Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function CategoryLabel({ category }: { category: ProjectCollateralCategory }) {
  return (
    <div
      className="w-fit items-center rounded-sm px-1.5 pb-0.5"
      style={{
        backgroundColor: colorByCollateralCategory[category],
      }}>
      <p
        className="text-[12px] font-medium uppercase leading-[18px]"
        style={{ color: '#FFEBEE' }}>
        {getProjectCollateralCategoryLabel(category)}
      </p>
    </div>
  )
}
